import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { FieldExplainerContent } from '@/content/field-explainers/types';

type FieldExplainerProps = {
  explainer: FieldExplainerContent;
  /** Horizontal anchor for the popover relative to the trigger. */
  align?: 'start' | 'end';
};

const PANEL_WIDTH_PX = 304; // 19rem
const VIEWPORT_MARGIN_PX = 12;
const TRIGGER_GAP_PX = 8;

type PanelPosition = {
  top: number;
  left: number;
  width: number;
};

/**
 * Tap-friendly "what does this calculate?" trigger + popover.
 *
 * Unlike the hover-only Tooltip primitive, this is a real button: it works on
 * touch, is keyboard focusable, closes on Escape and outside taps, and returns
 * focus to the trigger on close.
 *
 * The panel renders through a portal into document.body: result cards use the
 * shared Card component, whose overflow-hidden root would otherwise clip the
 * popover to the card's height. Position is measured from the trigger and
 * clamped to the viewport width so the panel stays fully visible on phones,
 * whichever edge the trigger sits near.
 */
export function FieldExplainer({ explainer, align = 'start' }: FieldExplainerProps) {
  const dialogId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const width = Math.min(PANEL_WIDTH_PX, viewportWidth - VIEWPORT_MARGIN_PX * 2);
    const preferredLeft = align === 'end' ? rect.right - width : rect.left;
    const clampedLeft = Math.max(
      VIEWPORT_MARGIN_PX,
      Math.min(preferredLeft, viewportWidth - VIEWPORT_MARGIN_PX - width)
    );

    setPosition({
      top: rect.bottom + window.scrollY + TRIGGER_GAP_PX,
      left: clampedLeft + window.scrollX,
      width
    });
  }, [align]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    } else {
      setPosition(null);
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  return (
    <span className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? dialogId : undefined}
        aria-label={`How ${explainer.label.toLowerCase()} is calculated`}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full border text-muted-foreground transition-colors',
          'hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          isOpen ? 'border-primary bg-primary text-primary-foreground hover:text-primary-foreground' : 'border-border bg-transparent'
        )}
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      {isOpen && position
        ? createPortal(
            <div
              ref={panelRef}
              id={dialogId}
              role="dialog"
              aria-label={`${explainer.label} explanation`}
              style={{ top: position.top, left: position.left, width: position.width }}
              className="absolute z-40 rounded-xl border border-border bg-popover p-4 text-left shadow-xl"
            >
              <FieldExplainerBody explainer={explainer} />
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

function FieldExplainerBody({ explainer }: { explainer: FieldExplainerContent }) {
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-display text-base text-popover-foreground">{explainer.label}</p>
        <Badge variant="outline" className="shrink-0 border-border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
          {explainer.kind}
        </Badge>
      </div>

      <p className="text-[13px] leading-relaxed text-popover-foreground">{explainer.plain}</p>

      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">How it is calculated</p>
        <pre className="overflow-x-auto whitespace-pre rounded-md border border-border/60 bg-background/70 px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground">
          {explainer.formula}
        </pre>
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">What it feeds</p>
        <p className="text-xs leading-relaxed text-muted-foreground">{explainer.feeds}</p>
      </div>

      {explainer.assumption ? (
        <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-amber-700 dark:text-amber-300">Assumption</span>
          <span className="text-xs leading-relaxed text-foreground">{explainer.assumption}</span>
        </div>
      ) : null}
    </div>
  );
}
